import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";

const roleHome = (role) => (role === "admin" ? "/admin" : role === "recruiter" ? "/recruiter" : "/candidate");

export const Login = () => {
  const { login, verifyOtp, resendOtp } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpState, setOtpState] = useState(null); // {otp_session_id, email_masked, expires_in_minutes}

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await login(email, password);
      if (res?.otp_required) {
        setOtpState({
          otp_session_id: res.otp_session_id,
          email_masked: res.email_masked,
          expires_in_minutes: res.expires_in_minutes,
        });
        toast.success(`Code sent to ${res.email_masked}`);
      } else {
        // Either legacy no-OTP path, or trusted device skipped OTP
        if (res?.trusted_device_skip) {
          toast.success("Welcome back — trusted device, no code needed");
        } else {
          toast.success("Welcome back");
        }
        navigate(roleHome(res.role));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/candidate";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <SEO title="Sign in" description="Sign in to Axiony to see AI match scores for every job." path="/login" />
      <div className="hidden lg:block relative grid-bg border-r border-white/10">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative p-16 h-full flex flex-col justify-between">
          <div className="mono-label">welcome back</div>
          <div>
            <h2 className="font-display text-4xl font-semibold tracking-tight max-w-md">
              Read signal, not résumés.
            </h2>
            <p className="mt-4 text-slate-400 max-w-md">
              Sign in to see match scores for every job, every applicant, every time.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {!otpState ? (
          <form onSubmit={submit} className="w-full max-w-md space-y-5">
            <div>
              <h1 className="font-display text-3xl font-semibold">Sign in</h1>
              <p className="text-sm text-slate-400 mt-2">Use your email or continue with Google.</p>
            </div>
            <Button type="button" onClick={googleLogin} variant="outline" className="w-full bg-transparent border-white/15 text-slate-100 hover:bg-white/5 hover:text-white rounded-lg py-6" data-testid="login-google-btn">
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                <path fill="#EA4335" d="M12 5c1.6 0 3 .6 4.1 1.6l3-3C17.2 1.6 14.8.5 12 .5 7.4.5 3.4 3.1 1.4 7l3.6 2.8C6 7 8.8 5 12 5z"/>
                <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.5-.2-2.3H12v4.3h6.4c-.3 1.5-1.1 2.7-2.4 3.5v2.9h3.9c2.3-2.1 3.6-5.2 3.6-8.4z"/>
                <path fill="#FBBC05" d="M5 14.2c-.2-.7-.4-1.4-.4-2.2s.1-1.5.4-2.2L1.4 7C.5 8.5 0 10.2 0 12s.5 3.5 1.4 5l3.6-2.8z"/>
                <path fill="#34A853" d="M12 23.5c3.2 0 5.9-1.1 7.9-2.9l-3.9-2.9c-1.1.7-2.4 1.2-4 1.2-3.2 0-5.9-2-6.9-4.9L1.4 17C3.4 20.9 7.4 23.5 12 23.5z"/>
              </svg>
              Continue with Google
            </Button>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <div className="flex-1 h-px bg-white/10" />
              <span>or</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="login-email-input" className="bg-[#111827] border-white/10 text-slate-100" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-[#06B6D4] hover:underline"
                >
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                     data-testid="login-password-input" className="bg-[#111827] border-white/10 text-slate-100" />
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg py-6">
              {busy ? "Sending code…" : "Continue"}
            </Button>
            <div className="text-sm text-slate-400 text-center">
              Don't have an account? <Link to="/signup" className="text-[#06B6D4] hover:underline">Sign up</Link>
            </div>
          </form>
        ) : (
          <OtpForm
            otpState={otpState}
            onBack={() => setOtpState(null)}
            onVerified={(user) => navigate(roleHome(user.role))}
            verifyOtp={verifyOtp}
            resendOtp={resendOtp}
            setOtpState={setOtpState}
          />
        )}
      </div>
    </div>
  );
};

const OtpForm = ({ otpState, onBack, onVerified, verifyOtp, resendOtp, setOtpState }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const verify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      const user = await verifyOtp(otpState.otp_session_id, code, trustDevice);
      toast.success(trustDevice ? "Welcome back — this device is trusted for 30 days" : "Welcome back");
      onVerified(user);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
      setCode("");
      inputRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setResending(true);
    try {
      const res = await resendOtp(otpState.otp_session_id);
      setOtpState({
        otp_session_id: res.otp_session_id,
        email_masked: res.email_masked,
        expires_in_minutes: res.expires_in_minutes,
      });
      toast.success(`New code sent to ${res.email_masked}`);
      setCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    } finally {
      setResending(false);
    }
  };

  return (
    <form onSubmit={verify} className="w-full max-w-md space-y-5" data-testid="otp-form">
      <button
        type="button"
        onClick={onBack}
        data-testid="otp-back-btn"
        className="text-sm text-slate-400 hover:text-white flex items-center gap-1"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back
      </button>

      <div>
        <div className="mono-label flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3 text-[#06B6D4]" /> two-factor sign-in
        </div>
        <h1 className="font-display text-3xl font-semibold mt-2">Check your email</h1>
        <p className="text-sm text-slate-400 mt-2 flex items-center gap-1.5">
          <Mail className="w-3.5 h-3.5" />
          We sent a 6-digit code to <span className="text-slate-200 font-mono">{otpState.email_masked}</span>
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-300">Verification code</Label>
        <Input
          ref={inputRef}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          data-testid="otp-code-input"
          placeholder="123456"
          className="bg-[#111827] border-white/10 text-slate-100 text-center text-2xl font-mono tracking-[0.5em] py-6"
        />
        <div className="text-xs text-slate-500">Code expires in {otpState.expires_in_minutes} minutes.</div>
      </div>

      <label className="flex items-start gap-2.5 cursor-pointer group select-none" data-testid="trust-device-row">
        <input
          type="checkbox"
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.target.checked)}
          data-testid="trust-device-checkbox"
          className="mt-1 h-4 w-4 rounded border-white/20 bg-[#111827] text-[#06B6D4] focus:ring-[#06B6D4] cursor-pointer"
        />
        <div className="text-sm">
          <div className="text-slate-200">Trust this device for 30 days</div>
          <div className="text-xs text-slate-500 mt-0.5">Skip the OTP step next time you sign in from this browser.</div>
        </div>
      </label>

      <Button
        type="submit"
        disabled={busy || code.length !== 6}
        data-testid="otp-verify-btn"
        className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg py-6"
      >
        {busy ? "Verifying…" : "Verify and sign in"}
      </Button>

      <div className="text-sm text-slate-400 text-center">
        Didn't get it?{" "}
        <button
          type="button"
          onClick={resend}
          disabled={resending}
          data-testid="otp-resend-btn"
          className="text-[#06B6D4] hover:underline disabled:opacity-60"
        >
          {resending ? "Sending…" : "Resend code"}
        </button>
      </div>
    </form>
  );
};
